/**
 * The search orchestrator (PLAN §3.1, §12.1 "Cancellation and streaming").
 *
 * Every query is a new GENERATION with its own AbortController; the previous
 * one is aborted before any provider is asked again. Providers run in
 * parallel: `local-sync` in the same call (no debounce), `local-ipc` after a
 * 30 ms trailing debounce, `server` after 200 ms and only from `minChars`.
 * Each batch is ranked and merged as it lands (a stream); a batch of a stale
 * generation is DROPPED here, so providers need no generation logic.
 * A provider's previous results stay until its new ones arrive (no flicker),
 * and a skeleton is flagged when an async provider has not answered in 80 ms.
 */
import { KIND_DEFAULTS, GLOBAL_LIMIT, GLOBAL_SHOWN, SCOPED_LIMIT, isAbortError } from './types';
import { rankAll, compareRanked } from './ranking';

export const SKELETON_MS = 80;

/** Scopes that also run sibling providers ("chat:" lists people and chats, §2.9). */
export const SCOPE_FAMILIES = Object.freeze({ chats: ['people'] });

function isAsyncIterable(x) {
  return x != null && typeof x[Symbol.asyncIterator] === 'function';
}

function isThenable(x) {
  return x != null && typeof x.then === 'function';
}

/** Which providers a query runs, in group order. */
export function activeProviders(providers, ctx, scope) {
  const avail = (p) => {
    try {
      return !!p.available(ctx);
    } catch {
      return false;
    }
  };
  if (scope) {
    const ids = new Set([scope, ...(SCOPE_FAMILIES[scope] || [])]);
    return providers.filter((p) => ids.has(p.id) && avail(p));
  }
  return providers.filter((p) => p.inGlobal !== false && avail(p));
}

/**
 * @param {{
 *   getProviders: () => object[],
 *   getCtx: () => object,
 *   onUpdate: (state: object) => void,
 *   rankOptions?: () => object,
 *   setTimer?: Function, clearTimer?: Function,
 * }} deps
 */
export function createSearchSession(deps) {
  const setTimer = deps.setTimer || ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer || ((id) => clearTimeout(id));
  let generation = 0;
  let controller = null;
  let timers = [];
  let query = null;
  let groups = new Map(); // providerId -> { results, status, more, skeleton, error }
  const stats = { dropped: 0, batches: 0 };
  let disposed = false;

  const snapshot = () => ({
    generation,
    query,
    groups: new Map(groups),
    stats: { ...stats },
  });
  const emit = (landed) => {
    if (!disposed) deps.onUpdate?.({ ...snapshot(), landed: landed || null });
  };

  function clearTimers() {
    for (const t of timers) clearTimer(t);
    timers = [];
  }

  function setGroup(id, patch) {
    const prev = groups.get(id) || { results: [], status: 'idle', more: false, skeleton: false, error: false };
    groups.set(id, { ...prev, ...patch });
  }

  function accept(gen, signal, provider, batch, append, scoped) {
    if (gen !== generation || signal.aborted || disposed) {
      stats.dropped += 1;
      return false;
    }
    stats.batches += 1;
    const limit = scoped ? SCOPED_LIMIT : GLOBAL_LIMIT;
    const opts = deps.rankOptions?.() || {};
    const ranked = rankAll(Array.isArray(batch) ? batch : [], query.terms, { ...opts, ctx: deps.getCtx() });
    const prev = groups.get(provider.id);
    let results = ranked;
    if (append && prev && prev.status !== 'stale') {
      const seen = new Set(prev.results.map((r) => r.id));
      results = [...prev.results, ...ranked.filter((r) => !seen.has(r.id))].sort(compareRanked);
    }
    results = results.slice(0, limit);
    setGroup(provider.id, {
      results,
      status: 'done',
      skeleton: false,
      error: false,
      more: !scoped && results.length > GLOBAL_SHOWN,
    });
    emit({ providerId: provider.id, generation: gen });
    return true;
  }

  async function invoke(gen, signal, provider, req, scoped) {
    if (gen !== generation || signal.aborted) return;
    let out;
    try {
      out = provider.search(req);
      if (isAsyncIterable(out)) {
        let first = true;
        // eslint-disable-next-line no-restricted-syntax
        for await (const batch of out) {
          if (gen !== generation || signal.aborted) {
            stats.dropped += 1;
            break;
          }
          accept(gen, signal, provider, batch, !first, scoped);
          first = false;
        }
        if (first && gen === generation && !signal.aborted) accept(gen, signal, provider, [], false, scoped);
        return;
      }
      if (isThenable(out)) out = await out;
      accept(gen, signal, provider, out, false, scoped);
    } catch (err) {
      if (isAbortError(err) || gen !== generation) return;
      if (provider.kind === 'server') {
        setGroup(provider.id, { results: [], status: 'error', error: true, skeleton: false, more: false });
      } else {
        // eslint-disable-next-line no-console
        console.warn(provider.id, err && err.name);
        setGroup(provider.id, { results: [], status: 'done', error: false, skeleton: false, more: false });
      }
      emit({ providerId: provider.id, generation: gen });
    }
  }

  function scheduleSkeleton(gen, id, ms) {
    timers.push(
      setTimer(() => {
        if (gen !== generation) return;
        const g = groups.get(id);
        if (g && g.status === 'loading' && !g.results.length) {
          setGroup(id, { skeleton: true });
          emit(null);
        }
      }, ms)
    );
  }

  /**
   * @param {{ scope: string|null, text: string, terms: string[], filters?: object }} q
   */
  function run(q) {
    if (disposed) return generation;
    controller?.abort();
    clearTimers();
    generation += 1;
    const gen = generation;
    controller = new AbortController();
    const { signal } = controller;
    query = { scope: q.scope || null, text: q.text || '', terms: q.terms || [], filters: q.filters || {} };
    const ctx = deps.getCtx();
    const list = activeProviders(deps.getProviders(), ctx, query.scope);
    const keep = new Set(list.map((p) => p.id));
    for (const id of [...groups.keys()]) if (!keep.has(id)) groups.delete(id);

    const syncRuns = [];
    // Providers that share a debounce fire from ONE timer, in one task, so the
    // index providers' calls reach the batching client together and become a
    // single `palette_search` per keystroke (index/batch.js).
    const due = new Map(); // debounce ms -> runs
    for (const p of list) {
      const defaults = KIND_DEFAULTS[p.kind] || KIND_DEFAULTS['local-sync'];
      const minChars = p.minChars ?? defaults.minChars;
      const debounce = p.debounceMs ?? defaults.debounceMs;
      const scoped = !!query.scope && (p.id === query.scope || (SCOPE_FAMILIES[query.scope] || []).includes(p.id));
      if (query.text.length < minChars) {
        setGroup(p.id, { results: [], status: 'idle', more: false, skeleton: false, error: false });
        continue;
      }
      const req = {
        text: query.text,
        terms: query.terms,
        filters: query.filters,
        scoped,
        limit: scoped ? SCOPED_LIMIT : GLOBAL_LIMIT,
        signal,
        ctx,
      };
      setGroup(p.id, { status: 'loading', skeleton: false, error: false });
      if (debounce > 0) {
        if (!due.has(debounce)) {
          const runs = [];
          due.set(debounce, runs);
          timers.push(setTimer(() => runs.forEach((fn) => fn()), debounce));
        }
        due.get(debounce).push(() => invoke(gen, signal, p, req, scoped));
      } else {
        syncRuns.push(() => invoke(gen, signal, p, req, scoped));
      }
      if (p.kind !== 'local-sync') scheduleSkeleton(gen, p.id, debounce + SKELETON_MS);
    }
    emit(null);
    for (const fn of syncRuns) fn();
    return gen;
  }

  return {
    run,
    /** Abort the running generation (close). */
    cancel() {
      controller?.abort();
      clearTimers();
      generation += 1;
    },
    dispose() {
      this.cancel();
      disposed = true;
    },
    get state() {
      return snapshot();
    },
    get generation() {
      return generation;
    },
  };
}
